/**
 * Portfolio Inbox — every active project's latest AI summary, RAG-sorted.
 * Admin-only. Lets implementation leads triage the whole portfolio in one place.
 */
import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const RAG_TONE = {
  red:   'bg-rose-50 border-rose-200 text-rose-900',
  amber: 'bg-amber-50 border-amber-200 text-amber-900',
  green: 'bg-emerald-50 border-emerald-200 text-emerald-900',
};

const RAG_DOT = {
  red:   'bg-rose-500',
  amber: 'bg-amber-500',
  green: 'bg-emerald-500',
};

export default function PMInbox() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [refreshState, setRefreshState] = useState({ status: 'idle' });
  const [polling, setPolling] = useState(false);
  const navigate = useNavigate();

  const load = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await axios.get(`${API}/pm/admin/inbox`);
      setRows(data || []);
    } finally { setLoading(false); }
  }, []);

  const loadStatus = useCallback(async () => {
    try {
      const { data } = await axios.get(`${API}/pm/admin/inbox/refresh/status`);
      setRefreshState(data);
      if (data.status === 'complete' || data.status === 'error') {
        setPolling(false);
        load();
      }
    } catch (e) {
      // Polling status endpoint; transient failures are expected during refresh windows.
      if (typeof console !== 'undefined') console.debug('inbox refresh status poll failed (ignored):', e?.message || e);
    }
  }, [load]);

  useEffect(() => { load(); loadStatus(); }, [load, loadStatus]);

  useEffect(() => {
    if (!polling) return;
    const t = setInterval(loadStatus, 5000);
    return () => clearInterval(t);
  }, [polling, loadStatus]);

  const triggerRefresh = async () => {
    try {
      await axios.post(`${API}/pm/admin/inbox/refresh`);
      setPolling(true);
      loadStatus();
    } catch (e) { window.alert('Could not start refresh'); }
  };

  const counts = rows.reduce((acc, r) => {
    const rag = r.summary?.rag;
    if (rag === 'red') acc.red++;
    else if (rag === 'amber') acc.amber++;
    else if (rag === 'green') acc.green++;
    else acc.none++;
    return acc;
  }, { red: 0, amber: 0, green: 0, none: 0 });

  return (
    <div className="min-h-screen bg-slate-50" data-testid="pm-inbox-page">
      <header className="bg-white border-b border-slate-200 px-6 py-4">
        <div className="container mx-auto flex items-center justify-between">
          <div>
            <p className="text-xs uppercase tracking-widest text-slate-500">Implementation</p>
            <h1 className="text-2xl font-semibold text-slate-900">Portfolio inbox</h1>
            <p className="text-sm text-slate-500 mt-1">Every active project's latest AI health summary, sorted red → amber → green.</p>
          </div>
          <div className="flex items-center gap-2">
            <button onClick={() => navigate('/admin/pm')} className="text-sm text-slate-500 hover:text-slate-900" data-testid="pm-inbox-back-btn">← All projects</button>
            <button
              onClick={triggerRefresh}
              disabled={polling || refreshState.status === 'running'}
              className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-sm rounded inline-flex items-center gap-2 disabled:opacity-50"
              data-testid="pm-inbox-refresh-btn"
            >
              {polling || refreshState.status === 'running' ? 'Refreshing…' : 'Refresh now'}
            </button>
          </div>
        </div>
      </header>

      <main className="container mx-auto px-6 py-6">
        {/* Counters */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-6">
          {[
            ['Red', counts.red, 'red'],
            ['Amber', counts.amber, 'amber'],
            ['Green', counts.green, 'green'],
            ['No summary yet', counts.none, null],
          ].map(([label, val, tone]) => (
            <div key={label} className={`border rounded p-3 ${tone ? RAG_TONE[tone] : 'bg-white border-slate-200 text-slate-600'}`}>
              <p className="text-xs uppercase tracking-wide opacity-70">{label}</p>
              <p className="text-2xl font-bold mt-1">{val}</p>
            </div>
          ))}
        </div>

        {/* Refresh-state banner */}
        {refreshState.status === 'running' && (
          <div className="mb-4 bg-blue-50 border border-blue-200 text-blue-900 text-sm px-4 py-2 rounded" data-testid="pm-inbox-running-banner">
            Refresh in progress — summaries appear here as each project completes (up to 10 per run; scheduler picks up the rest within the hour).
          </div>
        )}
        {refreshState.status === 'complete' && refreshState.result && (
          <div className="mb-4 bg-emerald-50 border border-emerald-200 text-emerald-900 text-sm px-4 py-2 rounded" data-testid="pm-inbox-complete-banner">
            Last refresh complete: <strong>{refreshState.result.refreshed}</strong> regenerated, <strong>{refreshState.result.skipped}</strong> skipped, <strong>{refreshState.result.failed}</strong> failed.
          </div>
        )}
        {refreshState.status === 'error' && (
          <div className="mb-4 bg-rose-50 border border-rose-200 text-rose-900 text-sm px-4 py-2 rounded" data-testid="pm-inbox-error-banner">
            Refresh failed: {refreshState.result?.error || 'unknown error'}
          </div>
        )}

        {/* Rows */}
        {loading ? (
          <p className="text-sm text-slate-400" data-testid="pm-inbox-loading">Loading…</p>
        ) : rows.length === 0 ? (
          <div className="bg-white border border-dashed border-slate-200 rounded p-8 text-center text-sm text-slate-500" data-testid="pm-inbox-empty">
            No active projects to triage.
          </div>
        ) : (
          <ul className="space-y-3" data-testid="pm-inbox-list">
            {rows.map((r) => {
              const rag = r.summary?.rag;
              return (
                <li key={r.project_id} data-testid={`pm-inbox-row-${r.project_id.slice(0, 8)}`}
                    className={`bg-white border rounded p-4 ${rag ? RAG_TONE[rag] : 'border-slate-200'}`}>
                  <div className="flex items-start gap-3">
                    <span className={`shrink-0 mt-1.5 w-3 h-3 rounded-full ${rag ? RAG_DOT[rag] : 'bg-slate-300'}`} aria-hidden />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 flex-wrap">
                        <Link to={`/admin/pm/${r.project_id}`} className="text-base font-semibold hover:underline" data-testid={`pm-inbox-link-${r.project_id.slice(0, 8)}`}>
                          {r.customer_org}
                        </Link>
                        {r.current_phase_name && <span className="text-xs opacity-70">· {r.current_phase_name}</span>}
                        {r.target_go_live && <span className="text-xs opacity-70">· go-live {r.target_go_live.slice(0, 10)}</span>}
                      </div>
                      {r.summary ? (
                        <>
                          {r.summary.headline && <p className="text-sm font-medium mt-1">{r.summary.headline}</p>}
                          {Array.isArray(r.summary.key_risks) && r.summary.key_risks.length > 0 && (
                            <ul className="list-disc pl-5 mt-1 text-xs space-y-0.5 opacity-90">
                              {r.summary.key_risks.slice(0, 3).map((k, i) => (
                                <li key={i}>{typeof k === 'string' ? k : (k.risk || k.title || JSON.stringify(k))}</li>
                              ))}
                            </ul>
                          )}
                          <p className="text-[11px] mt-2 opacity-60">
                            Last summary {r.summary.generated_at?.slice(0, 16).replace('T', ' ')} UTC · {r.summary.trigger || 'manual'}
                          </p>
                        </>
                      ) : (
                        <p className="text-xs opacity-70 mt-1">No summary yet — click "Refresh now" or open the project.</p>
                      )}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </main>
    </div>
  );
}
